// NOTICE: KAPTION-INTEGRATION
// whatsapp-message-merging — binds the merge queue (helper/kaptionMessageMerge.js)
// to the dashboard store.
//
// One queue per tab, shared by every ReplyBox instance, so a hold survives the
// reply box re-rendering. Held fragments show as ONE pending bubble with the
// same PENDING_UNDO status Undo uses (meta.kaptionMergeGroup marks it). On
// flush the bubble becomes an ordinary message through sendMessageWithData,
// carrying content_attributes.kaption_merge (id = echo_id = idempotency key).
import { computed, reactive } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import {
  KAPTION_DELIVERY_MODES,
  resolveKaptionDeliveryMode,
} from 'dashboard/helper/kaptionDeliveryMode';
import { createPendingMessage } from 'dashboard/helper/commons';
import { buildCreatePayload } from 'dashboard/api/inbox/message';
import {
  KaptionMessageMergeQueue,
  KAPTION_MERGE_FLUSH_REASONS,
  KAPTION_MERGE_LEVELS,
  resolveKaptionMergeLevel,
} from 'dashboard/helper/kaptionMessageMerge';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';

export const KAPTION_MESSAGE_MERGING_FLAG = 'KAPTION_MESSAGE_MERGING';
const WINDOW_MS = 24 * 60 * 60 * 1000;
const ACTIVITY = 2;

// Reactive mirror of the held groups, keyed by conversation id.
export const mergeState = reactive({ groups: {}, lastFlush: null });

let queue = null;
let boundStore = null;
// echo ids this tab sent; their ActionCable echo must not flush a newer hold.
const ownEchoIds = new Set();
const rememberEcho = id => {
  if (!id) return;
  ownEchoIds.add(id);
  if (ownEchoIds.size > 500) {
    ownEchoIds.delete(ownEchoIds.values().next().value);
  }
};

const findConversation = (store, conversationId) =>
  store.state.conversations?.allConversations?.find(
    c => c.id === conversationId
  );

const windowExpiresAt = (store, conversationId) => {
  const conversation = findConversation(store, conversationId);
  if (!conversation) return null;
  if (conversation.can_reply === false) return Date.now();
  const lastIncoming = [...(conversation.messages || [])]
    .reverse()
    .find(m => m.message_type === MESSAGE_TYPE.INCOMING);
  if (!lastIncoming?.created_at) return null;
  return lastIncoming.created_at * 1000 + WINDOW_MS;
};

const removePendingBubble = (store, conversationId, groupId) => {
  const conversation = findConversation(store, conversationId);
  if (!conversation) return;
  conversation.messages = conversation.messages.filter(m => m.id !== groupId);
};

const renderPendingBubble = (store, snapshot) => {
  store.commit('ADD_MESSAGE', {
    id: snapshot.id,
    echo_id: snapshot.id,
    conversation_id: snapshot.conversationId,
    content: snapshot.content,
    private: false,
    status: MESSAGE_STATUS.PENDING_UNDO,
    message_type: MESSAGE_TYPE.OUTGOING,
    created_at: Math.floor(snapshot.firstHeldAt / 1000),
    sender: snapshot.sender,
    attachments: null,
    content_attributes: {
      kaption_merge: { source_count: snapshot.count, pending: true },
    },
    meta: { kaptionMergeGroup: true, kaptionMergePaused: snapshot.paused },
  });
};

const sendMerged = async (store, payload, group) => {
  const pending = {
    ...createPendingMessage(payload),
    id: group.id,
    echo_id: group.id,
  };
  rememberEcho(group.id);
  mergeState.lastFlush = {
    conversationId: group.conversationId,
    reason: payload.contentAttributes.kaption_merge.flush_reason,
    count: group.fragments.length,
    at: Date.now(),
  };
  return store.dispatch('sendMessageWithData', pending);
};

// Last-chance flush when the tab goes away (plan §5: pagehide + keepalive).
// The request is idempotent on kaption_merge.id, so a later retry cannot
// double-send.
const flushWithKeepalive = () => {
  if (!queue) return;
  queue.conversationIds().forEach(conversationId => {
    const taken = queue.take(conversationId, KAPTION_MERGE_FLUSH_REASONS.LEAVE);
    if (!taken) return;
    const { payload, group } = taken;
    rememberEcho(group.id);
    // Keep the UI honest if the page is restored from the back/forward cache.
    queue.emit(conversationId, { removed: 'flush', groupId: group.id });
    boundStore?.commit('ADD_MESSAGE', {
      id: group.id,
      echo_id: group.id,
      conversation_id: conversationId,
      status: MESSAGE_STATUS.PROGRESS,
    });
    try {
      const { apiHost = '' } = window.chatwootConfig || {};
      const accountId = boundStore?.getters?.getCurrentAccountId;
      const headers = {
        'Content-Type': 'application/json',
        ...Object.fromEntries(
          Object.entries(window.axios?.defaults?.headers?.common || {}).filter(
            ([, value]) => typeof value === 'string'
          )
        ),
      };
      const body = buildCreatePayload({
        message: payload.message,
        isPrivate: false,
        contentAttributes: payload.contentAttributes,
        echoId: group.id,
      });
      window.fetch(
        `${apiHost}/api/v1/accounts/${accountId}/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          keepalive: true,
          credentials: 'same-origin',
          headers,
          body: JSON.stringify(body),
        }
      );
    } catch (error) {
      // best-effort: the tab is going away
    }
  });
};

const handleMessageCreated = message => {
  if (!queue || !message) return;
  const conversationId = message.conversation_id;
  if (!queue.hasGroup(conversationId)) return;
  if (message.private || message.message_type === ACTIVITY) return;
  if (message.echo_id && ownEchoIds.has(message.echo_id)) return;
  // The customer replied: what the agent held may no longer fit, so it is
  // paused (never auto-sent) until the agent confirms it.
  if (message.message_type === MESSAGE_TYPE.INCOMING) {
    queue.pause(conversationId, KAPTION_MERGE_FLUSH_REASONS.INCOMING);
    return;
  }
  queue.flush(conversationId, KAPTION_MERGE_FLUSH_REASONS.OTHER_SENDER);
};

const STATUS_ACTIONS = ['toggleStatus', 'assignAgent', 'assignTeam'];

export const ensureKaptionMergeQueue = store => {
  if (queue) return queue;
  boundStore = store;
  queue = new KaptionMessageMergeQueue({
    send: (payload, group) => sendMerged(store, payload, group),
    onChange: (conversationId, snapshot, meta) => {
      if (snapshot) {
        mergeState.groups[conversationId] = snapshot;
        renderPendingBubble(store, snapshot);
        return;
      }
      delete mergeState.groups[conversationId];
      if (meta?.removed === 'cancel') {
        removePendingBubble(store, conversationId, meta.groupId);
      }
    },
    onTyping: conversationId =>
      store.dispatch('conversationTypingStatus/toggleTyping', {
        status: 'on',
        conversationId,
        isPrivate: false,
      }),
    windowExpiresAt: conversationId => windowExpiresAt(store, conversationId),
  });
  store.subscribeAction({
    before: action => {
      if (action.type === 'sendMessageWithData') {
        rememberEcho(action.payload?.echo_id);
      }
      if (STATUS_ACTIONS.includes(action.type)) {
        const conversationId = action.payload?.conversationId;
        if (conversationId && queue.hasGroup(conversationId)) {
          queue.flush(conversationId, KAPTION_MERGE_FLUSH_REASONS.STATUS);
        }
      }
    },
    after: action => {
      if (action.type === 'addMessage') {
        handleMessageCreated(action.payload);
      }
    },
  });
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushWithKeepalive);
  }
  return queue;
};

// Test helper: forget the singleton between specs.
export const resetKaptionMergeQueue = () => {
  if (queue) {
    queue.conversationIds().forEach(id => queue.take(id, 'reset'));
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('pagehide', flushWithKeepalive);
  }
  queue = null;
  boundStore = null;
  ownEchoIds.clear();
  Object.keys(mergeState.groups).forEach(id => delete mergeState.groups[id]);
  mergeState.lastFlush = null;
};

export function useKaptionMessageMerge() {
  const store = useStore();
  const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
  const { canUseFeature } = useFeaturePermissions();
  const mergeQueue = ensureKaptionMergeQueue(store);

  const isFlagEnabled = computed(() =>
    isKaptionFlagEnabled(KAPTION_MESSAGE_MERGING_FLAG)
  );

  const accountSettings = computed(() => {
    const accountId = store.getters.getCurrentAccountId;
    return store.getters['accounts/getAccount']?.(accountId)?.settings || {};
  });

  const level = computed(() => {
    if (!isFlagEnabled.value) return KAPTION_MERGE_LEVELS.OFF;
    return resolveKaptionMergeLevel({
      uiSettings: store.getters.getUISettings,
      accountSettings: accountSettings.value,
    });
  });

  const isMerging = computed(() => level.value !== KAPTION_MERGE_LEVELS.OFF);

  // Undo is the floor of the hold: the same setting and permission the
  // ordinary Undo path reads.
  const undoMs = () => {
    const ui = store.getters.getUISettings || {};
    const undoOn =
      resolveKaptionDeliveryMode(ui) === KAPTION_DELIVERY_MODES.UNDO &&
      canUseFeature('undo_send');
    return undoOn ? (ui.kaption_undo_send_duration || 15) * 1000 : 0;
  };

  const groupFor = conversationId => mergeState.groups[conversationId] || null;

  const hold = payload =>
    mergeQueue.enqueue(payload, { level: level.value, undoMs: undoMs() });

  const flush = (conversationId, reason) =>
    mergeQueue.flush(conversationId, reason);

  const flushAll = reason => mergeQueue.flushAll(reason);

  const runAfterFlush = (conversationId, fn) =>
    mergeQueue.runAfterFlush(
      conversationId,
      KAPTION_MERGE_FLUSH_REASONS.NON_MERGEABLE,
      fn
    );

  const noteTyping = (conversationId, hasText) =>
    mergeQueue.noteTyping(conversationId, hasText);

  // Returns the removed fragment; the reply box puts it back in the composer.
  const undoLast = conversationId => mergeQueue.undoLast(conversationId);
  // Paused (customer replied): Deshacer takes the whole merged text back.
  const takeBack = conversationId => mergeQueue.takeBack(conversationId);

  return {
    mergeState,
    isFlagEnabled,
    level,
    isMerging,
    groupFor,
    hold,
    flush,
    flushAll,
    runAfterFlush,
    noteTyping,
    undoLast,
    takeBack,
  };
}
